import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import toastr from "toastr";
import Modal from "../../../../utils/components/Modal";
import Textarea from "../../../../utils/components/Textarea";
import Checkbox from "../../../../utils/components/Checkbox";
import Button from "../../../../utils/components/Button";
import Loading from "../../../../utils/components/Loading";
import {
  FiAlertCircle,
  FiAlertTriangle,
  FiArrowRight,
  FiCheck,
  FiClock,
  FiCopy,
  FiRepeat,
  FiRotateCw,
  FiX,
} from "react-icons/fi";
import ProcessingScreen from "../../../../utils/components/ProcessingScreen";
import DelegateTargetPicker from "./DelegateTargetPicker";
import { dative, genitive, possessive } from "../../../../utils/helpers/turkishSuffix";
import { bold, boldSuffixed } from "../../../../utils/helpers/boldSuffixed";
import API from "../../../../utils/API";
import { auth } from "../../../../App/routes";

export const DELEGATION_BATCH_SIZE = 100;
const LIST_LIMIT = 5;
const KEY = "company.users.delegate";

const STEP = {
  loading: "loading",
  loadError: "loadError",
  form: "form",
  processing: "processing",
  partialFailure: "partialFailure",
  batchSuccess: "batchSuccess",
  success: "success",
};

// TR texts use the suffixed forms (name_dat = "Burak Taş'a"); EN texts use the plain values
// Only plain values get suffixed forms; React elements (bold parts) pass their suffixed form explicitly
const isText = (value) => typeof value === "string" || typeof value === "number";
const withSuffixes = (data = {}) => ({
  ...(isText(data.name) && { name_dat: dative(data.name), name_gen: genitive(data.name) }),
  ...(isText(data.target) && { target_dat: dative(data.target) }),
  ...(isText(data.source) && { source_gen: genitive(data.source) }),
  ...(isText(data.count) && { count_poss: possessive(data.count) }),
  ...data,
});

const RESULT_STEPS = [STEP.partialFailure, STEP.batchSuccess, STEP.success];

// Result list: first rows plus an expandable "+N" row; each row opens its record in a new tab (design D11)
const TaskList = ({ rows, projectId, showStatus, pinned = 0, moreLabel, labels }) => {
  const [expanded, setExpanded] = useState(false);
  const collapsedCount = Math.max(LIST_LIMIT, pinned);
  const visible = expanded ? rows : rows.slice(0, collapsedCount);
  const hidden = rows.length - visible.length;

  const href = (row) =>
    row.message
      ? `${auth.tasks.link}/${row.task_id}?project_id=${projectId}`
      : auth.formBoxDetails.link.replace(":id", row.new_task_id);

  return (
    <div className="delegate-task-list border rounded">
      <ul className={`list-unstyled mb-0 ${expanded ? "is-expanded" : ""}`}>
        {visible.map((row) => (
          <li key={row.task_id} className={row.message ? "is-failed" : ""}>
            <a
              href={href(row)}
              target="_blank"
              rel="noopener noreferrer"
              className="d-flex justify-content-between align-items-center gap-3 px-3 py-2 text-reset text-decoration-none"
            >
              <div className="d-flex align-items-center gap-2 min-w-0">
                {showStatus &&
                  (row.message ? (
                    <FiX className="delegate-text-danger flex-shrink-0" />
                  ) : (
                    <FiCheck className="delegate-text-success flex-shrink-0" />
                  ))}
                <div className="min-w-0">
                  <div className="fw-bold">{row.form_no || `#${row.task_id}`}</div>
                  <small className={row.message ? "delegate-text-danger" : "text-secondary"}>
                    {row.form_name}
                    {row.message ? ` · ${row.message}` : ""}
                  </small>
                </div>
              </div>
              {showStatus ? (
                <span className={`delegate-soft-badge ${row.message ? "is-red" : "is-green"}`}>
                  {row.message ? labels.failed : labels.delegated}
                </span>
              ) : (
                row.form_status && <span className="delegate-soft-badge is-amber">{row.form_status}</span>
              )}
            </a>
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <button type="button" className="delegate-list-toggle btn btn-link w-100 text-start px-3 py-2" onClick={() => setExpanded(true)}>
          {moreLabel(hidden)}
        </button>
      )}
      {expanded && rows.length > collapsedCount && (
        <button type="button" className="delegate-list-toggle btn btn-link w-100 text-start px-3 py-2" onClick={() => setExpanded(false)}>
          {labels.showLess}
        </button>
      )}
    </div>
  );
};

TaskList.propTypes = {
  rows: PropTypes.array.isRequired,
  projectId: PropTypes.number,
  showStatus: PropTypes.bool,
  pinned: PropTypes.number,
  moreLabel: PropTypes.func.isRequired,
  labels: PropTypes.object.isRequired,
};

const DelegateTasksModal = ({ show, sourceUser, projectId, onClose, translate }) => {
  const t = (key, data) => translate(`${KEY}.${key}`, withSuffixes(data));

  const [step, setStep] = useState(STEP.loading);
  const [openTasks, setOpenTasks] = useState({ tasks: [], total: 0 });
  const [candidates, setCandidates] = useState({ users: [], groups: [], companies: [] });
  const [target, setTarget] = useState(null);
  const [description, setDescription] = useState("");
  const [notify, setNotify] = useState(true);
  const [succeeded, setSucceeded] = useState([]);
  const [failed, setFailed] = useState([]);
  const [lastRun, setLastRun] = useState({ succeeded: [], failed: [], notified: false });
  // All runs of the current batch (first send and retries)
  const [batchRun, setBatchRun] = useState({ succeeded: [], failed: [] });
  const [processingMessage, setProcessingMessage] = useState("");
  const [batchNumber, setBatchNumber] = useState(0);
  const taskInfo = useRef({});

  const loadOpenTasks = () =>
    API.getData(`user/open_tasks/${sourceUser.id}?project_id=${projectId}`).then(({ data: res }) => {
      const data = res.data || { tasks: [], total: 0 };
      data.tasks.forEach((task) => {
        taskInfo.current[task.task_id] = task;
      });
      return data;
    });

  useEffect(() => {
    if (!show || !sourceUser?.id || !projectId) return;

    setStep(STEP.loading);
    setTarget(null);
    setDescription("");
    setNotify(true);
    setSucceeded([]);
    setFailed([]);
    setBatchRun({ succeeded: [], failed: [] });
    setBatchNumber(0);
    taskInfo.current = {};

    Promise.all([loadOpenTasks(), API.getData(`task/initialize?project_id=${projectId}`)])
      .then(([tasks, { data: init }]) => {
        setOpenTasks(tasks);
        setCandidates({
          users: init.data?.users || [],
          groups: init.data?.groups || [],
          companies: init.data?.companies || [],
        });
        setStep(STEP.form);
      })
      .catch(() => setStep(STEP.loadError));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, sourceUser?.id, projectId]);

  const withInfo = (rows) => rows.map((row) => ({ ...taskInfo.current[row.task_id], ...row }));

  const finish = () => {
    if (step === STEP.processing) return;
    onClose({
      succeeded: withInfo(succeeded),
      failed: withInfo(failed),
      remaining: openTasks.total,
    });
  };

  const runBatch = async (taskIds, nextBatchNumber, { retry = false } = {}) => {
    const queued = Math.max(openTasks.total - taskIds.length, 0);
    setBatchNumber(nextBatchNumber);
    setProcessingMessage(
      nextBatchNumber > 1 || openTasks.total > DELEGATION_BATCH_SIZE
        ? t("processing.batch_message", { count: taskIds.length, target: target.label, batch: nextBatchNumber, queued })
        : t("processing.message", { count: taskIds.length, target: target.label }),
    );
    setStep(STEP.processing);

    // One request per batch; the server sends the notification for what it delegated (design D10)
    let runSucceeded = [];
    let runFailed = [];
    let notified = false;
    try {
      const { data: res } = await API.postData("tasks/delegate", {
        source_user_id: sourceUser.id,
        target_user_id: target.value,
        project_id: projectId,
        task_ids: taskIds,
        description,
        notify,
      });
      runSucceeded = res.data.succeeded;
      runFailed = res.data.failed;
      notified = Boolean(res.data.notified);
    } catch (err) {
      const message = err?.message || t("errors.request_failed");
      runFailed = taskIds.map((taskId) => ({ task_id: taskId, message }));
    }

    if (notify && runSucceeded.length > 0 && !notified) {
      toastr.warning(t("errors.notify_failed"));
    }

    let fresh = openTasks;
    try {
      fresh = await loadOpenTasks();
    } catch (err) {
      fresh = { ...openTasks, total: Math.max(openTasks.total - runSucceeded.length, 0) };
    }

    setOpenTasks(fresh);
    setSucceeded((prev) => [...prev, ...runSucceeded]);
    setFailed(runFailed);
    setLastRun({ succeeded: runSucceeded, failed: runFailed, notified });
    setBatchRun({ succeeded: retry ? [...batchRun.succeeded, ...runSucceeded] : runSucceeded, failed: runFailed });

    if (runFailed.length > 0) setStep(STEP.partialFailure);
    else if (fresh.total > 0) setStep(STEP.batchSuccess);
    else setStep(STEP.success);
  };

  const handleStart = () => {
    if (!target) {
      toastr.error(t("target_required"));
      return;
    }
    if (!openTasks.total) return;
    runBatch(openTasks.tasks.map((task) => task.task_id), 1);
  };

  const handleRetry = () => runBatch(batchRun.failed.map((row) => row.task_id), batchNumber, { retry: true });

  const handleContinue = async () => {
    setStep(STEP.loading);
    try {
      const fresh = await loadOpenTasks();
      setOpenTasks(fresh);
      if (!fresh.total) {
        setStep(STEP.success);
        return;
      }
      await runBatch(fresh.tasks.map((task) => task.task_id), batchNumber + 1);
    } catch (err) {
      setStep(STEP.batchSuccess);
    }
  };

  const listProps = (moreKey) => ({
    projectId,
    moreLabel: (count) => t(moreKey, { count }),
    labels: {
      delegated: t("result.status_delegated"),
      failed: t("result.status_failed"),
      showLess: t("result.show_less"),
    },
  });

  const Footer = ({ children }) => (
    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 border-top pt-3 mt-4">{children}</div>
  );

  const ResultHeader = ({ kind, title }) => (
    <div className="text-center mb-3">
      <div className={`delegate-status-icon is-${kind} mx-auto mb-3`}>
        {kind === "success" ? <FiCheck /> : <FiAlertTriangle />}
      </div>
      <h5 className="fw-bold mb-2">{title}</h5>
      <div className="d-flex align-items-center justify-content-center gap-2 fw-semibold">
        <span>{sourceUser?.name}</span>
        <FiArrowRight className="text-secondary" />
        <span>{target?.label}</span>
      </div>
    </div>
  );

  const NotifyLine = () =>
    lastRun.notified ? (
      <div className="text-center mb-3">
        <span className="delegate-pill is-green">
          <FiCheck /> {t("result.notified", { target: target?.label })}
        </span>
      </div>
    ) : null;

  const ListTitle = ({ children }) => <p className="delegate-list-title mb-2 text-uppercase small">{children}</p>;

  const renderForm = () => {
    const overBatch = openTasks.total > DELEGATION_BATCH_SIZE;
    const name = sourceUser?.name || "";
    return (
      <>
        {openTasks.total > 0 ? (
          <div className="delegate-info-band d-flex gap-2 mb-3">
            <span className="band-icon"><FiAlertCircle /></span>
            <div>
              {t("open_task_info", {
                name: bold(name),
                name_dat: boldSuffixed(name, dative),
                open_count: bold(t("open_task_count", { count: openTasks.total })),
              })}
            </div>
          </div>
        ) : (
          <div className="alert alert-secondary mb-3">{t("no_open_tasks", { name })}</div>
        )}
        {overBatch && (
          <div className="delegate-warning-band d-flex gap-2 mb-3">
            <span className="band-icon"><FiCopy /></span>
            <div>
              {t("batch_warning", {
                title: bold(t("batch_warning_title")),
                newest: bold(t("batch_newest")),
                remaining: bold(t("batch_remaining", { count: openTasks.total - DELEGATION_BATCH_SIZE })),
              })}
            </div>
          </div>
        )}

        <p className="mb-2 text-uppercase fw-bold">
          {t("target_label")} <span className="text-danger">*</span>
        </p>
        <DelegateTargetPicker
          candidates={candidates}
          sourceId={sourceUser?.id}
          value={target}
          onChange={setTarget}
          disabled={!openTasks.total}
          translate={translate}
        />
        <small className="text-secondary d-block mt-1">
          {t("target_hint", { single: bold(t("target_hint_single")) })}
        </small>

        <div className="mt-4">
          <Textarea
            title={t("note_label")}
            placeholder={overBatch ? t("note_placeholder_batch", { name }) : t("note_placeholder")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="delegate-note-input"
            rows={2}
            disabled={!openTasks.total}
          />
        </div>

        <div className="mt-3">
          <Checkbox checked={notify} onChange={setNotify} label={t("notify_label")} disabled={!openTasks.total} />
        </div>

        <Footer>
          <Button className="bg-white" onClick={finish}>
            {t("cancel")}
          </Button>
          <Button className="btn-orange text-white fw-bold" onClick={handleStart} disabled={!openTasks.total}>
            <FiRepeat className="me-2" />
            {overBatch ? t("submit_first_batch") : t("submit")}
          </Button>
        </Footer>
      </>
    );
  };

  const renderSuccess = () => (
    <>
      <ResultHeader kind="success" title={t("result.success_title", { count: succeeded.length })} />
      <NotifyLine />
      <p className="mb-3 text-center">
        <Link to={`${auth.formBox.link}?tab=sendbox&delegated=${succeeded.length}`} onClick={finish} className="delegate-link">
          {t("result.formbox_link")}
        </Link>
      </p>
      <ListTitle>{t("result.delegated_tasks", { count: succeeded.length })}</ListTitle>
      <TaskList rows={withInfo(succeeded)} {...listProps("result.more_tasks")} />
      <Footer>
        <span />
        <Button className="delegate-btn-blue text-white" onClick={finish}>
          {t("result.close")}
        </Button>
      </Footer>
    </>
  );

  const renderBatchSuccess = () => (
    <>
      <ResultHeader kind="success" title={t("result.success_title", { count: batchRun.succeeded.length })} />
      <NotifyLine />
      <div className="delegate-warning-band d-flex gap-2 mb-3">
        <span className="band-icon"><FiClock /></span>
        <div>
          <b>{t("result.remaining_title", { count: openTasks.total })}</b>{" "}
          {t("result.remaining_info", {
            source: sourceUser?.name,
            total: batchRun.succeeded.length + openTasks.total,
            count: batchRun.succeeded.length,
            remaining: openTasks.total,
          })}
        </div>
      </div>
      <ListTitle>{t("result.batch_tasks", { count: batchRun.succeeded.length })}</ListTitle>
      <TaskList rows={withInfo(batchRun.succeeded)} {...listProps("result.more_tasks_batch")} />
      <Footer>
        <button type="button" className="btn btn-link text-secondary text-decoration-none px-0" onClick={finish}>
          {t("result.continue_later")}
        </button>
        <Button className="btn-orange text-white fw-bold" onClick={handleContinue}>
          {t("result.continue", { count: openTasks.total })}
          <FiArrowRight className="ms-2" />
        </Button>
      </Footer>
    </>
  );

  const renderPartialFailure = () => {
    const runTotal = batchRun.succeeded.length + batchRun.failed.length;
    return (
      <>
        <ResultHeader
          kind="partial"
          title={t("result.partial_title", { succeeded: batchRun.succeeded.length, total: runTotal })}
        />
        <NotifyLine />
        <div className="d-flex justify-content-center flex-wrap gap-2 mb-3">
          <span className="delegate-pill is-green">
            <FiCheck /> {t("result.partial_succeeded", { count: batchRun.succeeded.length })}
          </span>
          <span className="delegate-pill is-red">
            <FiX /> {t("result.partial_failed", { count: batchRun.failed.length })}
          </span>
        </div>
        <p className="text-secondary small mb-3">{t("result.partial_info", { source: sourceUser?.name })}</p>
        <ListTitle>{t("result.tasks", { count: runTotal })}</ListTitle>
        <TaskList
          rows={withInfo([...batchRun.failed, ...batchRun.succeeded])}
          pinned={batchRun.failed.length}
          showStatus
          {...listProps("result.more_tasks_succeeded")}
        />
        <Footer>
          <Button className="bg-white" onClick={finish}>
            {t("result.close")}
          </Button>
          <Button className="btn-orange text-white fw-bold" onClick={handleRetry}>
            <FiRotateCw className="me-2" />
            {t("result.retry", { count: batchRun.failed.length })}
          </Button>
        </Footer>
      </>
    );
  };

  const renderBody = () => {
    switch (step) {
      case STEP.loading:
      case STEP.processing:
        return <Loading classNames="my-4" />;
      case STEP.loadError:
        return (
          <>
            <div className="alert alert-danger">{t("errors.load_failed")}</div>
            <Footer>
              <span />
              <Button className="btn-secondary" onClick={finish}>
                {t("result.close")}
              </Button>
            </Footer>
          </>
        );
      case STEP.form:
        return renderForm();
      case STEP.partialFailure:
        return renderPartialFailure();
      case STEP.batchSuccess:
        return renderBatchSuccess();
      case STEP.success:
        return renderSuccess();
      default:
        return null;
    }
  };

  return (
    <>
      <Modal
        show={show && step !== STEP.processing}
        title={t("title", { name: <span key="title-name" className="text-orange">{sourceUser?.name || ""}</span> })}
        modalSize="lg"
        setShow={finish}
        disableButtons={step === STEP.processing}
        hideHeader={RESULT_STEPS.includes(step)}
      >
        <div className="DelegateTasksModal">{renderBody()}</div>
      </Modal>
      <ProcessingScreen
        show={show && step === STEP.processing}
        title={t("processing.title")}
        message={processingMessage}
      />
    </>
  );
};

DelegateTasksModal.propTypes = {
  show: PropTypes.bool,
  sourceUser: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  projectId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
};

export default DelegateTasksModal;
